import { useLocation } from 'react-router-dom';
import { useScrollTopOnLocationChange } from '@leagueplatform/web-common';
import { APP_NAV_DEFAULT_HEIGHT } from 'components/header-nav/navbar/nav.constants';
import pageHeaderBanner from 'assets/page-header-banner.svg';
import pageHeaderMobileBanner from 'assets/page-header-mobile-banner.svg';

const DEFAULT_HEADER_WIDTH_DESKTOP = '20024px';

interface UseBasePageProps {
  isPrivatePage?: boolean;
}

interface UseBasePageReturn {
  // Data
  pathname: string;

  // Computed values
  shouldShowNavBar: boolean;
  shouldShowFooter: boolean;
  mainStyles: any;

  // Actions
  initializePage: () => void;
}

export const useBasePage = ({
  isPrivatePage = true,
}: UseBasePageProps): UseBasePageReturn => {
  useScrollTopOnLocationChange();
  const { pathname } = useLocation();

  const shouldShowNavBar = isPrivatePage;
  const shouldShowFooter = isPrivatePage && !pathname.startsWith('/messages/');

  const mainStyles = {
    overflowX: 'hidden',
    position: 'relative',
    flex: '1',
    minHeight: `calc(100vh - ${APP_NAV_DEFAULT_HEIGHT}px)`,
    backgroundColor: '$surfaceBackgroundPrimary',
    'a[href^="tel:"]': {
      cursor: 'default',
      pointerEvents: 'none',
      color: '$textPrimary',
      '&:hover': {
        color: '$textPrimary',
        textDecoration: 'none',
      },
    },
    '.cc-page-header-background::before': {
      zIndex: '1',
      backgroundImage: `url(${pageHeaderBanner})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'auto',
      backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 33vw) bottom`,
      '@desktop': {
        backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 42vw) bottom`,
      },
      '@laptop': {
        backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 35vw) bottom`,
      },
      '@tablet': {
        backgroundImage: `url(${pageHeaderMobileBanner})`,
        backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 30vw) bottom`,
      },
      '@mobileLandscape': {
        backgroundImage: `url(${pageHeaderMobileBanner})`,
        backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 20vw) bottom`,
      },
      '@mobile': {
        backgroundImage: `url(${pageHeaderMobileBanner})`,
        backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 50vw) bottom`,
      },
    },
    '.cc-page-header-background > *': {
      zIndex: '2',
      position: 'relative',
    },
    '.cc-page-header-background ~ *': {
      zIndex: '2',
      position: 'relative',
    },
    // Make carousel cards equal width
    '[data-testid="carousel"] ul > li': {
      flex: 1,
    },
    ...(pathname.startsWith('/benefits')
      ? {
          // Specific styles for benefits pages
          '.cc-page-header-background::before': {
            height: '19970px',
            zIndex: '1',
            backgroundImage: `url(${pageHeaderBanner})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'auto',
            backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 33vw) bottom`,
            '@desktop': {
              backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 42vw) bottom`,
            },
            '@laptop': {
              backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 35vw) bottom`,
            },
            '@tablet': {
              backgroundImage: `url(${pageHeaderMobileBanner})`,
              backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 30vw) bottom`,
            },
            '@mobileLandscape': {
              backgroundImage: `url(${pageHeaderMobileBanner})`,
              backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 20vw) bottom`,
            },
            '@mobile': {
              backgroundImage: `url(${pageHeaderMobileBanner})`,
              backgroundPosition: `left calc(100% - ${DEFAULT_HEADER_WIDTH_DESKTOP}/2 + 50vw) bottom`,
            },
          },
        }
      : {}),
  };

  const initializePage = () => {
    // Any additional page initialization logic can go here
  };

  return {
    // Data
    pathname,

    // Computed values
    shouldShowNavBar,
    shouldShowFooter,
    mainStyles,

    // Actions
    initializePage,
  };
};
